<template>
 <div class="collection-info-section" :class="{'--open': isOpen}">
   <div class="collection-info-section__head collection-reviews__head" @click="changeOpen">
     <span class="h5 semibold">{{ label }}</span>
     <div class="collection-reviews__info">
       <img src="~/assets/img/svg/common/star.svg" alt=""/>
       {{ reviewsRating }}
       <span class="--count">{{reviewsCount}}</span>
     </div>
     <img class="--arrow" src="~/assets/img/svg/common/arrow-down.svg" alt=""/>
   </div>
   <div class="collection-info-section__body collection-reviews__body">
     <button class="collection-reviews__button btn btn-primary" @click="openCreateReview">
       Написать отзыв
     </button>
     <template
       v-for="(item, index) in reviews"
       :key="`reviews-${index}`"
     >
       <ReviewCardSmall :item="item"/>
       <div
        v-if="index !== reviews.length - 1"
        class="collection-reviews__body-separator"
       />
     </template>
   </div>
 </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import { useCreateReviewModal } from '~/composables/useCreateReviewModal';
import ReviewCardSmall from "~/components/reviews/ReviewCardSmall.vue";

const props = defineProps({
  product: {
    type: Object as any,
    default: () => {}
  },
  model: {
    type: String,
  },
  page: {
    type: String,
  },
});
const {
  $api,
  $caseWords,
  $numberFormat,
} = useNuxtApp();
const {
  openCreateReviewModal
} = useCreateReviewModal();

const label = computed(() => {
  return Boolean(props.page === 'Product') ? 'Отзывы о товаре' : 'Отзывы о коллекции'
});

const reviewsCount = computed(() => {
  const count = Number.parseFloat(props?.product?.reviewsCount || 0);
  const word = $caseWords(count, ['отзыв','отзыва','отзывов'])
  return [count, word].join(" ")
});
const reviewsRating = computed(() => {
  return $numberFormat((props?.product?.reviewsRating || 0), 1, '.')
});
const reviewsRatingPercent = computed(() => {
  const count = Number.parseFloat((props?.product?.reviewsRating || 0));
  return 100 * count / 5
});

const isOpen = ref(false);
const changeOpen = () => {
  isOpen.value = !isOpen.value;
};

const reviews: any = ref([]);
const getReviews = async () => {
  const res = await $api.agent.get(`/reviews?filter[model]=${props.model}&filter[model_key]=${props.product.id}&filter[type]=review&expand=files`).then((res) => {
    return res._data
  }).catch(() => {
    return []
  });
  reviews.value = res;
};
onMounted(async () => {
  await getReviews();
});

const openCreateReview = () => {
  openCreateReviewModal(props.product);
};

function buildReviewsJsonLd(list: any[]) {
  if (!list?.length) return null;
  const strip = (s: string) => (typeof s === 'string' ? s : '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const fmt = (ts: number | undefined) => {
    if (ts == null || Number.isNaN(Number(ts))) return '';
    const d = new Date(Number(ts) * 1000);
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  };
  return {
    '@context': 'https://schema.org',
    '@graph': list.map((item: any) => {
      const plain = strip(item.text || '');
      return {
        '@type': 'Review',
        reviewRating: { '@type': 'Rating', worstRating: 0, bestRating: 5, ratingValue: Number(item.rating) || 0 },
        itemReviewed: { '@type': 'Thing', name: plain.slice(0, 35) },
        author: { '@type': 'Person', name: item.name || '' },
        datePublished: fmt(item.created_at),
        reviewBody: plain
      };
    })
  };
}
const reviewsJsonLd = computed(() => buildReviewsJsonLd(reviews.value || []));
useHead(computed(() => {
  const data = reviewsJsonLd.value;
  if (!data) return {};
  return {
    script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(data), key: 'reviews-jsonld' }]
  };
}));
</script>

<style lang="scss" scoped>
.collection-reviews__head {
  justify-content: initial;

  .--arrow {
    margin-left: auto;
  }
}
.collection-reviews__info {
  display: flex;
  align-items: center;
  margin-left: auto;
  color: #989898;
  font-size: 12px;
  font-weight: 500;
  line-height: 100%;

  img {
    width: 13px;
    height: 13px;
    margin-right: 6px;
    transform: initial!important;
  }

  .--count {
    margin-left: 8px;
    margin-bottom: -2px;
    border-bottom: 1px solid;
    color: rgb(152 152 152 / 100%);

    &:hover {
      border-bottom: 1px solid transparent;
    }
  }
}
.collection-reviews__button {
  width: 100%;
  height: 60px;
  margin-bottom: 24px;
  border-radius: 16px;
  text-align: center;
  font-weight: 600;
}

.review-card {
  padding: 24px 0;
  box-sizing: border-box;
  border-top: 1px solid #EBEBEB;
}
.review-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .--name {
    color: #000000;
  }
  .--rating {
    position: relative;
    z-index: 0;
    width: calc(18px * 5);
    height: 18px;

    &:after {
      content: "";
      position: absolute;
      top: 0; left: 0;
      height: 100%; width: 100%;
      z-index: 1;
      background-image: url("@/assets/img/svg/common/rating.svg");
      background-size: contain;
    }
    &:before {
      content: "";
      position: absolute;
      top: 0; left: 0;
      height: 100%;
      width: var(--percent-active);
      z-index: 2;
      background-image: url("@/assets/img/svg/common/rating.svg");
      background-size: contain;
      filter: invert(93%) sepia(15%) saturate(4081%) hue-rotate(296deg) brightness(89%) contrast(110%);
    }
  }
}
.review-card__body {
  margin-top: 16px;
  color: #989898;
}
.collection-reviews__body-separator {
  margin: 16px 0;
  width: 100%;
  height: 1px;
  background-color: #F4F3F1;
}

@media (max-width: 1023px) {
  .collection-reviews__button {
    height: 48px;
    border-radius: 12px;
  }
}
</style>
